import { MODES, LocationStore } from './state';
import { AutocompleteMode, bindFormValidation } from './autocomplete';
import { openLocationMapModal } from './map-modal';
import { reverseGeocode } from './geocoder';

export { MODES, LocationSlot, LocationStore } from './state';
export { AutocompleteMode, bindFormValidation } from './autocomplete';
export { getLocationMapModal, openLocationMapModal } from './map-modal';

/**
 * Reads initial location / extra values from `gon.search_params` for a role.
 * Returns `{ location, extra }` where each value is the raw param (ID or name
 * string) that still needs to be resolved against the locations dataset.
 */
function readInitialData(role) {
  const params = (typeof gon !== 'undefined' && gon.search_params) || {};
  const areaKey = `${role}_area`;
  const portKey = `${role}_port`;
  const hasArea = params[areaKey] && params[areaKey] !== '';

  return {
    location: hasArea ? params[areaKey] : params[portKey],
    extra: hasArea ? params[portKey] : null,
  };
}

/**
 * Apply initial map-pick state from gon.search_params when the request had coords
 * (e.g. re-render after validation error). Sets slot map pick so the form shows
 * the address and hidden inputs. Searches made before origin_address /
 * destination_address were persisted carry no address, so those fall back to
 * reverse-geocoding the coords.
 */
function applyInitialMapPickFromParams(store, params) {
  if (!params || typeof params !== 'object') return;
  const latKey = (role) => `${role}_lat`;
  const lngKey = (role) => `${role}_lng`;
  const displayKey = (role) => `${role}_address`;
  for (const role of ['origin', 'destination']) {
    const lat = params[latKey(role)];
    const lng = params[lngKey(role)];
    if (lat == null || lat === '' || lng == null || lng === '') continue;
    const numLat = parseFloat(lat);
    const numLng = parseFloat(lng);
    if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) continue;
    const slot = store.getSlot(role);
    if (!slot) continue;
    const displayAddressFromParams = params[displayKey(role)];
    const displayAddress = displayAddressFromParams || `${numLat}, ${numLng}`;
    slot.setMapPick({ lat: numLat, lng: numLng, displayAddress });

    if (!displayAddressFromParams) {
      reverseGeocode(numLat, numLng).then((result) => {
        if (result?.display_name) {
          slot.setMapPick({ lat: numLat, lng: numLng, displayAddress: result.display_name });
        }
      }).catch(() => {});
    }
  }
}

/**
 * Central class for location picking. Owns a {@link LocationStore} with
 * paired origin / destination slots and orchestrates input modes
 * (autocomplete, map-picker, geocoding).
 *
 * Phase 1 exposes only autocomplete; map and geocoding are registered in
 * later phases via {@link registerMode}.
 */
export class LocationPicker {
  constructor(locations = []) {
    this._store = new LocationStore(locations);
    this._modes = new Map();
    this._initialized = false;
  }

  get store() { return this._store; }
  get locations() { return this._store.locations; }
  get origin() { return this._store.origin; }
  get destination() { return this._store.destination; }

  /**
   * Create origin and destination slots, seeded with values from
   * `gon.search_params`. Safe to call more than once (no-ops after first).
   */
  init() {
    if (this._initialized) return this;

    this._store.createSlot('origin', readInitialData('origin'));
    this._store.createSlot('destination', readInitialData('destination'));

    const params = (typeof gon !== 'undefined' && gon.search_params) || {};
    applyInitialMapPickFromParams(this._store, params);

    this._initialized = true;

    return this;
  }

  // ── Mode orchestration ────────────────────────────────────

  /**
   * Register a mode handler. A handler is an object implementing:
   *   { name: string, attach(slot, store): void, detach(slot): void }
   *
   * `attach` is called when the mode becomes active on a slot.
   * `detach` is called when switching away from that mode.
   */
  registerMode(handler) {
    this._modes.set(handler.name, handler);
  }

  /**
   * Switch a slot to the given mode, calling detach/attach on the
   * previous and new handlers.
   */
  activateMode(role, modeName) {
    const slot = this._store.getSlot(role);
    const handler = this._modes.get(modeName);
    if (!slot || !handler) return;

    const prev = this._modes.get(slot.mode);
    if (prev && prev.detach) prev.detach(slot);

    slot.setMode(modeName);
    handler.attach(slot, this._store);
  }

  // ── Event shorthand ───────────────────────────────────────

  /** Subscribe to all slot change events at the store level. */
  onChange(fn) {
    this._store.on('slot:change', fn);
    return this;
  }

  // ── Location resolution ───────────────────────────────────

  /**
   * Resolve a raw query (numeric ID string or display name) to a location
   * item from the dataset. Returns `null` if no match is found.
   */
  resolveLocation(query) {
    if (!query) return null;
    const locations = this._store.locations;

    const numId = Number(query);
    if (Number.isInteger(numId) && numId > 0) {
      return locations.find(l => l.id === numId) || null;
    }

    const term = String(query).toLowerCase();
    const normalizeAr = s => s.replace(/[أإ]/g, 'ا').toLowerCase().trim();

    return locations.find(
      l => l.value.toLowerCase().trim() === term
        || (l.value_ar && normalizeAr(l.value_ar) === normalizeAr(term))
    ) || null;
  }

  /**
   * Populate a slot's location/extra from its `initialData` by resolving
   * raw param values against the locations dataset.
   */
  hydrateSlot(role) {
    const slot = this._store.getSlot(role);
    if (!slot) return;

    const { location, extra } = slot.initialData;

    const resolved = this.resolveLocation(location);
    if (resolved) slot.setLocation(resolved);

    const resolvedExtra = this.resolveLocation(extra);
    if (resolvedExtra) slot.setExtra(resolvedExtra);
  }

  /**
   * Hydrate both origin and destination slots.
   */
  hydrateAll() {
    this.hydrateSlot('origin');
    this.hydrateSlot('destination');
  }
}

/**
 * Backward-compatible entry point matching the original signature:
 *
 *   createAutoCompleteInput({ locations })
 *
 * Creates a fully initialized LocationPicker, registers and activates the
 * autocomplete mode on both slots, and binds form-level validation.
 * Hydration of initial values from `gon.search_params` is handled inside
 * the autocomplete mode's attach lifecycle.
 */
export function createAutoCompleteInput({ locations }) {
  const picker = new LocationPicker(locations);
  picker.init();

  picker.registerMode(AutocompleteMode);
  picker.activateMode('origin', MODES.AUTOCOMPLETE);
  picker.activateMode('destination', MODES.AUTOCOMPLETE);

  bindFormValidation(picker);
  bindMapButton(picker);

  return picker;
}

/**
 * Store the picker on the compare form and bind "pick from map" button
 * clicks. When the user confirms a location in the modal, the corresponding
 * slot is updated and the autocomplete DOM sync reflects it.
 */
function bindMapButton(picker) {
  const $form = $('#compare form').length ? $('#compare form') : $('.component-search__form');
  if (!$form.length) return;

  $form.data('locationPicker', picker);
  $form.on('click', '.js-location-map-trigger', function (e) {
    e.preventDefault();
    const role = $(this).data('locationRole');
    const slot = role ? picker.store.getSlot(role) : null;
    if (!slot) return;

    const currentForCenter = slot.isMapPick
      ? slot.mapPick
      : (slot.location && !slot.location.port ? slot.location : slot.extra && !slot.extra.port ? slot.extra : null);

    openLocationMapModal({
      locations: picker.locations,
      currentLocation: currentForCenter && typeof currentForCenter.lat === 'number' && typeof currentForCenter.lng === 'number' ? currentForCenter : null,
      onSelect(pick) {
        const s = picker.store.getSlot(role);
        s.clearExtra();
        s.clearLocation();
        s.setMapPick(pick);
      },
    });
  });
}
